import { useState } from 'react';
import api from 'src/services/api';

// ─────────────────────────────────────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SaleItem {
  _id: string;
  sale_hash: string;
  transaction_id?: string | number;
  payment_status_id: string;
  payment_method_id: string;
  total: number;           // centavos
  net_total: number;       // centavos
  payer_id_response?: { name?: string };
  client_id_response?: { name?: string };
  createdAt?: string;
  approved_at?: string;
}

export interface FinanceItem {
  item_id: number;
  hash: string;
  client: string;
  product: string;
  total: number | null;         // centavos (bruto)
  net_total: number;            // centavos (líquido)
  saldo: number;                // centavos
  sacado: number;
  status: string;
  operation_type: string;
  operation_type_raw: string | null;
  payment_method_id: string;
  fees: number | null;               // juros de parcelamento
  tax: number | null;                // taxa de venda
  administration_tax: number | null; // taxa administrativa
  transaction_tax: number | null;    // taxa de cartão
  antecipation_gap: number;          // gap de antecipação (pode ser positivo ou negativo)
  operation_fee: number;             // taxa TED
  date: string;
  dateRef?: string;   // data de criação/solicitação — usada para ordenação correta de TEDs
  approved_at?: string;
  createdAt?: string;
  client_id?: string | number;   // ID do cliente/pagador — usado para buscar histórico completo
}

// ─────────────────────────────────────────────────────────────────────────────
//  Resultado do relatório
// ─────────────────────────────────────────────────────────────────────────────

export interface ChargebackMatch {
  // Venda original
  sale: SaleItem | null;
  // Entrada de crédito original no extrato
  originalCredit: FinanceItem | null;
  // Entrada de chargeback (débito)
  chargebackEntry: FinanceItem;
  // Entrada de chargeback_refund (devolução de taxas pelo marketplace), se houver
  refundEntry: FinanceItem | null;

  // Valores calculados (centavos)
  saleBruto: number;           // bruto da venda original
  creditoLiquido: number;      // líquido que havia sido creditado
  debitadoChargeback: number;  // absoluto do débito do chargeback
  taxasDevolvidas: number;     // chargeback_refund (crédito de volta das taxas)
  custoOperacao: number;       // debitado − líquido original (custo extra do chargeback)
  prejuizoTotal: number;       // saleBruto + custoOperacao (o que o cliente perdeu de fato)
}

export interface NotConciliatedSale {
  sale: SaleItem;
  estimatedAvailableAt: Date | null;
  isPastDeadline: boolean;
}

export interface FinanceReport {
  // ── BLOCO 1: Universo de vendas ──────────────────────────────────────────
  totalSalesBruto: number;
  totalConciliated: number;
  totalNotConciliated: number;
  totalCancelledInFinance: number;

  salesConciliated: SaleItem[];
  salesNotConciliated: NotConciliatedSale[];
  salesCancelledInFinance: SaleItem[];

  // ── BLOCO 2: Composição do líquido ──────────────────────────────────────
  brutoConciliated: number;
  feesTotal: number;
  taxTotal: number;
  adminTaxTotal: number;
  transactionTaxTotal: number;
  antecipationGapTotal: number;
  chargebackDebitTotal: number;
  chargebackRefundTotal: number;
  netChargebackImpact: number;
  liquidoCreditEntries: number;   // líquido puro das vendas (sem ajuste de CB)
  liquidoTotal: number;           // liquidoCreditEntries − netChargebackImpact
  diferencaBrutoLiquido: number;

  // ── BLOCO 3: Movimentação do saldo ──────────────────────────────────────
  totalSacado: number;
  totalTaxasTED: number;
  saldoAtual: number;

  // ── BLOCO 4: Chargebacks detalhados ─────────────────────────────────────
  chargebacks: ChargebackMatch[];
  totalCustoOperacaoChargebacks: number;
  totalPrejuizoChargebacks: number;

  // ── Saques ───────────────────────────────────────────────────────────────
  saqueEntries: FinanceItem[];

  // ── Divergências ─────────────────────────────────────────────────────────
  // Créditos no extrato cujo hash não corresponde a NENHUMA venda no banco
  creditosOrfaos: FinanceItem[];
  totalCreditosOrfaos: number;

  // Linhas onde saldo[i-1] + net_total[i] ≠ saldo[i] (inconsistência de saldo)
  saldoInconsistencies: Array<{
    item: FinanceItem;
    prevSaldo: number;
    expectedSaldo: number;
    diff: number;
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────

const PAID_METHODS = new Set(['pix', 'boleto', 'credit_card']);

function normalizeMethod(method?: string | null): string {
  if (!method) return '';
  const m = method.toLowerCase().trim();
  if (m === 'pix') return 'pix';
  if (m === 'boleto') return 'boleto';
  if (m === 'credit_card' || m === 'creditcard' || m.includes('crédit') || m.includes('credit')) return 'credit_card';
  return m;
}

function addBusinessDays(date: Date, days: number): Date {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const dow = result.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return result;
}

function estimateAvailableDate(sale: SaleItem): Date | null {
  const base = sale.approved_at ? new Date(sale.approved_at) : null;
  if (!base) return null;
  const method = normalizeMethod(sale.payment_method_id);
  if (method === 'credit_card') { const d = new Date(base); d.setDate(d.getDate() + 32); return d; }
  if (method === 'boleto') return addBusinessDays(base, 3);
  if (method === 'pix') return addBusinessDays(base, 1);
  return null;
}

function n(v: number | null | undefined): number {
  return Number(v) || 0;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useFinanceCheck() {
  const [salesData, setSalesData] = useState<SaleItem[]>([]);
  const [financeData, setFinanceData] = useState<FinanceItem[]>([]);
  const [report, setReport] = useState<FinanceReport | null>(null);
  const [loading, setLoading] = useState(false);

  const loadGlobal = async (project_id: string) => {
    setLoading(true);
    try {
      const [checkRes, salesRes] = await Promise.all([
        api.get(`/check-saldo-static/${project_id}/json`),
        api.get(`/sales`, { params: { limitItems: 100000, where: { project_id } } }),
      ]);

      const finance: FinanceItem[] = checkRes?.data?.rows ?? [];
      const sales: SaleItem[] = salesRes?.data?.rows ?? [];

      setSalesData(sales);
      setFinanceData(finance);
      setReport(buildReport(sales, finance));
    } finally {
      setLoading(false);
    }
  };

  return { loadGlobal, salesData, financeData, report, loading };
}

// ─────────────────────────────────────────────────────────────────────────────
//  buildReport
// ─────────────────────────────────────────────────────────────────────────────

export function buildReport(sales: SaleItem[], finance: FinanceItem[]): FinanceReport {
  const now = new Date();

  // ── Índices para lookup O(1) ───────────────────────────────────────────────
  const financeByHash = new Map<string, FinanceItem[]>();
  for (const f of finance) {
    if (!financeByHash.has(f.hash)) financeByHash.set(f.hash, []);
    financeByHash.get(f.hash)!.push(f);
  }

  const saleByHash = new Map<string, SaleItem>();
  for (const s of sales) saleByHash.set(s.sale_hash, s);

  // ── BLOCO 1: Vendas pagas ─────────────────────────────────────────────────
  const paidSales = sales.filter(
    s => s.payment_status_id === 'paid' &&
         PAID_METHODS.has(normalizeMethod(s.payment_method_id)) &&
         n(s.total) > 0
  );

  const salesConciliated: SaleItem[] = [];
  const salesNotConciliated: NotConciliatedSale[] = [];

  for (const sale of paidSales) {
    const entries = financeByHash.get(sale.sale_hash) ?? [];
    const creditEntry = entries.find(e => e.operation_type_raw === 'credit');
    if (creditEntry) {
      salesConciliated.push(sale);
    } else {
      const estimatedAvailableAt = estimateAvailableDate(sale);
      salesNotConciliated.push({
        sale,
        estimatedAvailableAt,
        isPastDeadline: estimatedAvailableAt ? estimatedAvailableAt <= now : false,
      });
    }
  }

  const creditEntries = finance.filter(f => f.operation_type_raw === 'credit');
  const salesCancelledInFinance: SaleItem[] = [];
  for (const entry of creditEntries) {
    const sale = saleByHash.get(entry.hash);
    if (sale && sale.payment_status_id !== 'paid') {
      salesCancelledInFinance.push(sale);
    }
  }

  // Créditos no extrato cujo hash não tem NENHUMA venda correspondente no banco
  // (caso distinto de salesCancelledInFinance, onde a venda existe mas tem status ≠ paid)
  const creditosOrfaos = creditEntries.filter(entry => !saleByHash.has(entry.hash));
  const totalCreditosOrfaos = creditosOrfaos.reduce((a, f) => a + n(f.total), 0);

  const totalSalesBruto         = paidSales.reduce((a, s) => a + n(s.total), 0);
  const totalConciliated        = salesConciliated.reduce((a, s) => a + n(s.total), 0);
  const totalNotConciliated     = salesNotConciliated.reduce((a, r) => a + n(r.sale.total), 0);
  const totalCancelledInFinance = salesCancelledInFinance.reduce((a, s) => a + n(s.total), 0);

  // ── BLOCO 2: Composição do líquido ───────────────────────────────────────
  const brutoConciliated    = creditEntries.reduce((a, f) => a + n(f.total), 0);
  const feesTotal           = creditEntries.reduce((a, f) => a + n(f.fees), 0);
  const taxTotal            = creditEntries.reduce((a, f) => a + n(f.tax), 0);
  const adminTaxTotal       = creditEntries.reduce((a, f) => a + n(f.administration_tax), 0);
  const transactionTaxTotal = creditEntries
    .reduce((a, f) => a + n(f.transaction_tax), 0);

  // ← NOVO: soma do antecipation_gap de todas as entradas de crédito
  // Valor positivo = desconto aplicado (reduz o líquido)
  // Valor negativo = crédito aplicado (aumenta o líquido)
  const antecipationGapTotal = creditEntries.reduce((a, f) => a + n(f.antecipation_gap), 0);

  const chargebackDebits  = finance.filter(f => f.operation_type_raw === 'chargeback' || f.operation_type_raw === 'refund');
  const chargebackRefunds = finance.filter(f => f.operation_type_raw === 'chargeback_refund');

  const chargebackDebitTotal  = chargebackDebits.reduce((a, f) => a + Math.abs(n(f.net_total)), 0);
  const chargebackRefundTotal = chargebackRefunds.reduce((a, f) => a + n(f.net_total), 0);
  const netChargebackImpact   = chargebackDebitTotal - chargebackRefundTotal;

  const liquidoCreditEntries = creditEntries.reduce((a, f) => a + n(f.net_total), 0);
  const liquidoTotal         = liquidoCreditEntries - netChargebackImpact;
  const diferencaBrutoLiquido = brutoConciliated - liquidoTotal;

  // ── Inconsistências de saldo ─────────────────────────────────────────────
  // Para cada linha com operation_type_raw definido, verifica:
  // saldo_anterior + net_total deve ≈ saldo (tolerância 1 centavo)
  const SALDO_TOLERANCIA = 1;
  const saldoInconsistencies: FinanceReport['saldoInconsistencies'] = [];
  const financeOrdenado = [...finance].sort(
    (a, b) => new Date(a.dateRef ?? a.date).getTime() - new Date(b.dateRef ?? b.date).getTime()
  );
  let prevSaldo = 0;
  for (const item of financeOrdenado) {
    if (item.operation_type_raw == null) { prevSaldo = item.saldo ?? prevSaldo; continue; }
    // Para TEDs, o saldo cai por net_total + operation_fee (a taxa é debitada separadamente do saque)
    const netEfetivo  = item.operation_type_raw === 'ted'
      ? n(item.net_total) - n(item.operation_fee)
      : n(item.net_total);
    const expectedSaldo = prevSaldo + netEfetivo;
    const realSaldo     = item.saldo ?? 0;
    const diff          = realSaldo - expectedSaldo;
    if (Math.abs(diff) > SALDO_TOLERANCIA) {
      saldoInconsistencies.push({ item, prevSaldo, expectedSaldo, diff });
    }
    prevSaldo = realSaldo;
  }

  // ── BLOCO 3: Saques ───────────────────────────────────────────────────────
  const saqueEntries  = finance.filter(f => f.operation_type_raw === 'ted');
  const totalSacado   = saqueEntries.reduce((a, f) => a + Math.abs(n(f.net_total)), 0);
  const totalTaxasTED = saqueEntries.reduce((a, f) => a + n(f.operation_fee), 0);
  const saldoAtual    = finance.length > 0 ? finance[finance.length - 1].saldo : 0;

  // ── BLOCO 4: Chargebacks detalhados ──────────────────────────────────────
  const chargebacks: ChargebackMatch[] = chargebackDebits.map(cbEntry => {
    const entries        = financeByHash.get(cbEntry.hash) ?? [];
    const originalCredit = entries.find(e => e.operation_type_raw === 'credit') ?? null;
    const refundEntry    = entries.find(e => e.operation_type_raw === 'chargeback_refund') ?? null;
    const sale           = saleByHash.get(cbEntry.hash) ?? null;

    const saleBruto          = n(sale?.total);
    const creditoLiquido     = n(originalCredit?.net_total);
    const debitadoChargeback = Math.abs(n(cbEntry.net_total));
    const taxasDevolvidas    = n(refundEntry?.net_total);
    const custoOperacao      = debitadoChargeback - taxasDevolvidas - creditoLiquido;
    const prejuizoTotal      = saleBruto + Math.max(0, custoOperacao);

    return {
      sale,
      originalCredit,
      chargebackEntry: cbEntry,
      refundEntry,
      saleBruto,
      creditoLiquido,
      debitadoChargeback,
      taxasDevolvidas,
      custoOperacao,
      prejuizoTotal,
    };
  });

  const totalCustoOperacaoChargebacks = chargebacks.reduce((a, c) => a + Math.max(0, c.custoOperacao), 0);
  const totalPrejuizoChargebacks      = chargebacks.reduce((a, c) => a + c.prejuizoTotal, 0);

  return {
    totalSalesBruto,
    totalConciliated,
    totalNotConciliated,
    totalCancelledInFinance,
    salesConciliated,
    salesNotConciliated,
    salesCancelledInFinance,

    brutoConciliated,
    feesTotal,
    taxTotal,
    adminTaxTotal,
    transactionTaxTotal,
    antecipationGapTotal,
    chargebackDebitTotal,
    chargebackRefundTotal,
    netChargebackImpact,
    liquidoCreditEntries,
    liquidoTotal,
    diferencaBrutoLiquido,

    totalSacado,
    totalTaxasTED,
    saldoAtual,

    chargebacks,
    totalCustoOperacaoChargebacks,
    totalPrejuizoChargebacks,

    saqueEntries,

    creditosOrfaos,
    totalCreditosOrfaos,

    saldoInconsistencies,
  };
}